import { type FC, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@/shared/components";
import {
  FormBuilder,
  lockSchema,
  SECRET_PLACEHOLDER,
} from "@/shared/components/Form";
import {
  useCurrentPlatformQuery,
  useConnectPayPalMutation,
  useDisconnectPayPalMutation,
} from "@/partner/entities/platforms";
import {
  interpolationFormat,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";
import { makePayPalSettingsSchema } from "../model/schemas";

/**
 * Коды отказа бекенда (extensions.code) и тексты под них, как у Stripe,
 * Paynet и CCBill. Сообщение сервера наружу не идёт.
 */
const CONNECT_ERROR_KEYS: Record<string, string> = {
  INVALID_PAYPAL_SETTINGS: "paymentSettings.paypalErrors.invalidSettings",
  INVALID_PAYPAL_CREDENTIALS: "paymentSettings.paypalErrors.invalidCredentials",
  PAYPAL_API_ERROR: "paymentSettings.paypalErrors.apiError",
  PAYMENT_PROVIDER_NOT_AVAILABLE: "paymentSettings.providerNotAllowedError",
};

const CONNECT_FALLBACK_KEY = "paymentSettings.paypalErrors.fallback";
const DISCONNECT_FALLBACK_KEY = "paymentSettings.disconnectError";

/**
 * Отказ отключения: захваты PayPal ещё ждут исхода (PENDING с captureId),
 * их COMPLETED или DENIED пришёл бы уже без кредов и вебхука.
 */
const DISCONNECT_ERROR_KEYS: Record<string, string> = {
  PAYPAL_HAS_PENDING_CAPTURES: "paymentSettings.paypalErrors.pendingCaptures",
};

/**
 * Блок подключения PayPal, 1:1 по образцу CCBillSettingsCard, рядом с
 * которым рендерится (см. PlatformSettings.tsx). Секрет наружу не отдаётся
 * даже маской целиком (см. схему PlatformPayPalSettings): поле clientSecret
 * в locked-состоянии заполняется SECRET_PLACEHOLDER, а не сохранённым
 * значением, редактировать можно после «Отключить». clientId публичный,
 * показывается целиком.
 *
 * Отличие от CCBill: адрес вебхука регистрирует у PayPal сам бекенд, партнёр
 * никуда его не копирует, поэтому подпись под ним об этом и предупреждает
 * (в отличие от CCBill, где партнёр обязан вписать адрес в админку сам).
 * Переключатель Sandbox относится к аккаунту именно этой платформы, а не ко
 * всему сервису, поэтому подписан явно.
 */
export const PayPalSettingsCard: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch } = useCurrentPlatformQuery();
  // Отключение необратимо для вебхука и прерывает приём оплаты: сначала
  // подтверждение.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const paypal = data?.currentPlatform?.paymentSettings?.paypal;
  // Пока PayPal подключён, поля только показывают сохранённые значения
  // (секрет точками); редактировать можно после «Отключить».
  const locked = Boolean(paypal?.connectedAt);
  const schema = useMemo(() => {
    const base = makePayPalSettingsSchema(t);
    return locked ? lockSchema(base) : base;
  }, [t, locked]);

  const [connectPayPal, { loading: connecting }] = useConnectPayPalMutation({
    onCompleted: () => {
      enqueueSnackbar(t("paymentSettings.paypalConnectedSnackbar"), {
        variant: "success",
      });
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(
        t(
          resolveMutationErrorKey(
            error,
            CONNECT_ERROR_KEYS,
            CONNECT_FALLBACK_KEY
          )
        ),
        { variant: "error" }
      );
    },
  });

  const [disconnectPayPal, { loading: disconnecting }] =
    useDisconnectPayPalMutation({
      onCompleted: () => {
        setConfirmOpen(false);
        enqueueSnackbar(t("paymentSettings.paypalDisconnectedSnackbar"), {
          variant: "success",
        });
        refetch();
      },
      onError: (error) => {
        setConfirmOpen(false);
        enqueueSnackbar(
          t(
            resolveMutationErrorKey(
              error,
              DISCONNECT_ERROR_KEYS,
              DISCONNECT_FALLBACK_KEY
            )
          ),
          { variant: "error" }
        );
      },
    });

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("paymentSettings.paypalTitle")}
        action={
          paypal?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="success"
                label={t("paymentSettings.paypalConnectedAt", {
                  date: interpolationFormat(
                    paypal.connectedAt,
                    "dateTime",
                    i18n.language
                  ),
                })}
              />
              <Chip size="small" variant="outlined" label={paypal.clientId} />
            </Stack>
          ) : (
            <Chip
              size="small"
              label={t("paymentSettings.paypalNotConnected")}
            />
          )
        }
      />
      <CardContent>
        {paypal?.webhookUrl && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>
              {t("paymentSettings.paypalWebhookUrlLabel")}
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
            >
              {paypal.webhookUrl}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              {t("paymentSettings.paypalWebhookUrlHint")}
            </Typography>
          </Alert>
        )}
        <FormBuilder
          key={locked ? "locked" : "editable"}
          formId="paypal-settings-form"
          schema={schema}
          initialValues={{
            clientId: paypal?.clientId ?? "",
            clientSecret: locked ? SECRET_PLACEHOLDER : "",
            sandbox: paypal?.sandbox ?? false,
          }}
          onSubmit={async (values) => {
            await connectPayPal({
              variables: {
                input: {
                  clientId: String(values.clientId),
                  clientSecret: String(values.clientSecret),
                  sandbox: Boolean(values.sandbox),
                },
              },
            });
          }}
          submitButton={!locked}
          resetButton={false}
          submitButtonText={t("paymentSettings.connectPayPalButton")}
          disabled={connecting || locked}
          actionsAlign="end"
        />
        {paypal?.connectedAt && (
          <Button
            variant="outlined"
            color="error"
            sx={{ mt: 2 }}
            disabled={disconnecting}
            onClick={() => setConfirmOpen(true)}
          >
            {t("paymentSettings.disconnectPayPalButton")}
          </Button>
        )}
        <ConfirmDialog
          open={confirmOpen}
          loading={disconnecting}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            void disconnectPayPal();
          }}
          confirmTitle={t("paymentSettings.paypalDisconnectConfirmTitle")}
          confirmText={t("paymentSettings.paypalDisconnectConfirmText")}
        />
      </CardContent>
    </Card>
  );
};
